// <ToastProvider> — top-right toasts per DESIGN.md §7.6.
//
// 8s auto-dismiss for info/success; persistent for warning (manual dismiss
// recommended for operator attention); persistent for danger. Danger toasts
// get role="alert"; others role="status".

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Toast } from "./Toast";
import {
  ToastContext,
  type ToastApi,
  type ToastInput,
  type ToastItem,
  type ToastTone,
} from "./toast-types";
import "./Toast.css";

const AUTO_DISMISS_MS_SHORT = 8_000;

let idCounter = 0;
function nextId(): string {
  idCounter += 1;
  return `t-${Date.now().toString(36)}-${idCounter}`;
}

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timeoutsRef = useRef<Map<string, number>>(new Map());

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
    const timeouts = timeoutsRef.current;
    const handle = timeouts.get(id);
    if (handle !== undefined) {
      window.clearTimeout(handle);
      timeouts.delete(id);
    }
  }, []);

  const clearAll = useCallback(() => {
    setItems([]);
    const timeouts = timeoutsRef.current;
    timeouts.forEach((handle) => window.clearTimeout(handle));
    timeouts.clear();
  }, []);

  const push = useCallback(
    (tone: ToastTone, input: ToastInput): void => {
      const id = nextId();
      setItems((prev) => [...prev, { ...input, id, tone }]);
      if (tone === "info" || tone === "success") {
        const handle = window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS_SHORT);
        timeoutsRef.current.set(id, handle);
      }
    },
    [dismiss],
  );

  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      timeouts.forEach((handle) => window.clearTimeout(handle));
      timeouts.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      info: (t) => push("info", t),
      success: (t) => push("success", t),
      warning: (t) => push("warning", t),
      danger: (t) => push("danger", t),
      dismiss,
      clearAll,
    }),
    [push, dismiss, clearAll],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="rb-toast-region" aria-live="polite" aria-atomic="false">
        {items.map((t) => (
          <Toast key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
